import type { UseReminderPrefsResult } from "../hooks/useReminderPrefs";
import { HELP_COPY } from "../lib/helpCopy";
import { UNSUPPORTED_HINT } from "../lib/reminderGuidance";
import { InfoTooltip } from "./InfoTooltip";
import { guidanceFor, ReminderGuidance } from "./ReminderGuidance";
import { TestNotificationButton } from "./TestNotificationButton";

export interface ReminderToggleRowProps {
  reminderPrefs: UseReminderPrefsResult;
}

/**
 * Sprint-005 C6 — the More tab's self-serve "Daily reminders" row.
 * Reuses the Admin console's own toggle-row/switch markup+CSS
 * (`.admin-toggle-row`/`.admin-switch`, see
 * `views/PersonDetailOverlay.tsx`) — purely a shared visual pattern, not
 * a role-gated surface; every authed person (member/manager/admin) sees
 * this row.
 *
 * What it shows depends on what this device can do (`useReminderPrefs`):
 * - push works here — the switch, plus "Send me a test notification" once
 *   it's on;
 * - iPhone/iPad in a browser tab (`needs_install`) or on old iOS
 *   (`needs_update`) — the steps to get there, no switch (there's nothing
 *   to turn on yet);
 * - blocked by the phone/browser (`denied`) — a disabled switch plus the
 *   device-specific steps to unblock it (the app can't re-ask);
 * - a browser with no push at all — one honest line, no switch.
 * Hidden only while the first check is still running.
 */
export function ReminderToggleRow({ reminderPrefs }: ReminderToggleRowProps) {
  const { support, device, busy, error, enable, disable } = reminderPrefs;

  if (support === "checking") return null;

  const label = (
    <span class="admin-toggle-label">
      Daily reminders
      <InfoTooltip
        label={HELP_COPY.dailyReminders.label}
        content={HELP_COPY.dailyReminders.content}
      />
    </span>
  );
  const guidance = guidanceFor(support, device);

  if (support === "unsupported" || support === "needs_install" || support === "needs_update") {
    return (
      <div class="admin-toggle-row reminder-row-guidance">
        <span>
          {label}
          {guidance ? (
            <ReminderGuidance guidance={guidance} />
          ) : (
            <span class="admin-toggle-hint">{UNSUPPORTED_HINT}</span>
          )}
        </span>
      </div>
    );
  }

  const isOn = support === "subscribed";
  const isBrowserBlocked = support === "denied";

  function handleClick() {
    if (busy || isBrowserBlocked) return;
    void (isOn ? disable() : enable());
  }

  return (
    <div class="admin-toggle-row">
      <span>
        {label}
        {guidance ? (
          <ReminderGuidance guidance={guidance} />
        ) : (
          <span class="admin-toggle-hint">
            {isOn ? "You'll get a morning reminder." : "Reminders are off."}
          </span>
        )}
        {error ? (
          <span class="reminder-error-hint" role="alert">
            {error}
          </span>
        ) : null}
        {isOn ? <TestNotificationButton /> : null}
      </span>
      <button
        type="button"
        class={`admin-switch${isOn ? " is-on" : ""}`}
        role="switch"
        aria-checked={isOn}
        aria-label={`Daily reminders — ${isOn ? "on" : "off"}`}
        disabled={busy || isBrowserBlocked}
        onClick={handleClick}
      >
        <span class="admin-switch-knob" aria-hidden="true" />
      </button>
    </div>
  );
}
